import { useMemo } from 'react'
import { useCurriculumCompletionContext } from './curriculumCompletionContext'
import {
  applyLessonCompletionToLinkSummaries,
  buildLmsUnitForUnitPage,
  selectClassRegistrationFromResponse,
} from 'pages/UnitV3/data'
import {
  buildLeafNodeIdMap,
  getOrderedLeafNodeIdsFromCourseTree,
} from 'utils/courseTreeHelpers'

/**
 * Derives lesson completion status for each link summary on a unit page by
 * reading the current instructor registration data from the LMS store.
 *
 * Fetches /v2/instructor/me if the required program slice is not yet in the
 * store. Also fetches the curriculum-service course tree via the coursePointer
 * so that Contentful entry IDs (courseNodeEntryPoint) can be translated to LMS
 * leaf node IDs for accurate completion matching — mirroring the pattern used
 * by `useMarkCompleteV3`.
 */
export const useUnitLessonCompletion = ({
  instructorDataKey,
  linkSummaries,
  siteId: explicitSiteId,
  gradeSegment,
  courseCode,
  coursePointer,
  contentGroupCode,
  unitId,
}) => {
  // Use shared context for common data fetching and resolution
  const {
    resolvedSiteId,
    classId,
    instructorData,
    courseCodeForTree: contextCourseCodeForTree,
    courseTreeData,
    transformedCourseTree,
    isLoading: contextIsLoading,
  } = useCurriculumCompletionContext({
    instructorDataKey,
    coursePointer,
    contentGroupCode,
    explicitSiteId,
  })

  // Use registration course code or fallback to provided courseCode
  const courseCodeForTree = useMemo(
    () => contextCourseCodeForTree || courseCode,
    [contextCourseCodeForTree, courseCode],
  )

  // --- Unit-specific processing ---

  const leafNodeIdMap = useMemo(() => {
    if (!transformedCourseTree) return {}
    return buildLeafNodeIdMap(transformedCourseTree)
  }, [transformedCourseTree])

  /**
   * Ordered list of leaf node IDs taken directly from
   * `subTreeNodes.siteEducator[0].leafExternalIds` in the course tree response.
   *
   * This is the primary completion-matching path for programs (e.g. DWB) where
   * curriculum-service Summary entries do not include `courseNodeConnector`.
   * The position of each entry matches the position of the corresponding link
   * summary so completion can be resolved by index.
   */
  const orderedLeafNodeIds = useMemo(
    () => getOrderedLeafNodeIdsFromCourseTree(courseTreeData),
    [courseTreeData],
  )

  // --- Class registration + completion ---

  const classRegistration = useMemo(
    () =>
      selectClassRegistrationFromResponse({
        instructorResponse: instructorData,
        classId,
        siteId: resolvedSiteId,
        gradeSegment,
        courseCode: courseCodeForTree,
      }),
    [instructorData, classId, resolvedSiteId, gradeSegment, courseCodeForTree],
  )

  const enrichedLinkSummaries = useMemo(
    () =>
      applyLessonCompletionToLinkSummaries({
        linkSummaries,
        classRegistration,
        leafNodeIdMap,
        orderedLeafNodeIds,
      }),
    [linkSummaries, classRegistration, leafNodeIdMap, orderedLeafNodeIds],
  )

  const lmsUnit = useMemo(
    () =>
      buildLmsUnitForUnitPage({
        classRegistration,
        courseTreeData,
        unitId,
      }),
    [classRegistration, courseTreeData, unitId],
  )

  return {
    linkSummaries: enrichedLinkSummaries,
    isLoading: contextIsLoading,
    lmsUnit,
  }
}
